import React, { useCallback, useEffect, useState } from 'react';
import { api, apiUpload, Card, fmtDay } from './api';

const API = process.env.REACT_APP_API_URL || '';

// Prevention, in two lines: the risk analysis on file with its risks in the
// Risk register, and the privacy reviews of every vendor and system holding
// member information. Nothing shows when nothing is due.
export default function PreventionPanel({ refresh }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null); // 'analysis' | 'reviews' | null
  const load = useCallback(async () => { const r = await api('/prevention'); if (r.success) setD(r); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const a = d.risk_analysis; const rk = d.risks || {}; const rv = d.reviews || {}; const mm = d.mismatches || [];
  const needed = (rv.needed || []).length;
  const analysisTone = !a ? 'text-red-700' : a.overdue ? 'text-red-700' : 'text-slate-700';
  const after = async () => { await load(); if (refresh) await refresh(); };
  return (
    <Card title="Prevention">
      <div className="divide-y divide-slate-100">
        <div className="py-2.5 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-slate-800 w-44">Risk analysis</span>
          <span className={`text-sm ${analysisTone}`}>
            {!a ? 'None on file.' : `Filed ${fmtDay(a.filed_on)}${a.filed_by_name ? ` by ${a.filed_by_name}` : ''} · ${rk.open || 0} risk${rk.open === 1 ? '' : 's'} open · next due ${fmtDay(a.next_due)}${a.overdue ? ' (overdue)' : ''}`}
          </span>
          <button type="button" onClick={() => setOpen(open === 'analysis' ? null : 'analysis')} className="ml-auto text-xs font-semibold text-orange-700">{open === 'analysis' ? 'Close' : (a ? 'Open' : 'File the analysis')}</button>
        </div>
        <div className="py-2.5 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-slate-800 w-44">Vendor and system reviews</span>
          <span className={`text-sm ${needed || mm.length ? 'text-amber-800' : 'text-slate-700'}`}>
            {needed ? `${needed} need${needed === 1 ? 's' : ''} a review` : 'Every vendor and system holding member information is reviewed'}{rv.sealed ? ` · ${rv.sealed} sealed` : ''}{mm.length ? ` · ${mm.length} name${mm.length === 1 ? '' : 's'} not in the FDR registry` : ''}
          </span>
          {(needed || mm.length || rv.sealed) ? <button type="button" onClick={() => setOpen(open === 'reviews' ? null : 'reviews')} className="ml-auto text-xs font-semibold text-orange-700">{open === 'reviews' ? 'Close' : 'Open'}</button> : null}
        </div>
      </div>
      {open === 'analysis' && <RiskAnalysis d={d} after={after} />}
      {open === 'reviews' && <Reviews d={d} after={after} />}
    </Card>
  );
}

function RiskAnalysis({ d, after }) {
  const a = d.risk_analysis;
  const [risks, setRisks] = useState(null);
  const [filing, setFiling] = useState(!a);
  const [file, setFile] = useState(null);
  const [filedOn, setFiledOn] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [rf, setRf] = useState({ title: '', description: '', severity: '', likelihood: '' });
  const loadRisks = useCallback(async () => { const r = await api('/prevention/risks'); if (r.success) setRisks(r.risks); }, []);
  useEffect(() => { loadRisks(); }, [loadRisks]);

  const fileIt = async () => {
    if (!file) { setErr('Choose the analysis document.'); return; }
    setBusy(true); setErr('');
    const fd = new FormData(); fd.append('file', file); fd.append('filed_on', filedOn); fd.append('notes', notes);
    const r = await apiUpload('/prevention/risk-analysis', fd);
    setBusy(false);
    if (!r.success) { setErr(r.message || 'Could not file it'); return; }
    setFiling(false); setFile(null); setNotes(''); await after();
  };
  const addRisk = async () => {
    if (!rf.title.trim()) { setErr('Name the risk.'); return; }
    setBusy(true); setErr('');
    const r = await api(`/prevention/risk-analysis/${a.id}/risks`, { method: 'POST', body: { title: rf.title, description: rf.description, severity_score: rf.severity ? Number(rf.severity) : null, likelihood_score: rf.likelihood ? Number(rf.likelihood) : null } });
    setBusy(false);
    if (!r.success) { setErr(r.message || 'Could not add it'); return; }
    setRf({ title: '', description: '', severity: '', likelihood: '' }); setAdding(false); await loadRisks(); await after();
  };
  const inp = 'text-sm border border-slate-300 rounded-lg px-3 py-1.5 text-slate-800 bg-white';
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-4">
      {a && !filing && (
        <div className="flex items-center gap-3 flex-wrap text-sm text-slate-700">
          <a href={`${API}/api/privacy-program/prevention/risk-analysis/${a.id}/file`} className="font-semibold text-orange-700 no-underline">{a.file_name}</a>
          <span className="text-slate-400">·</span>
          <span>Covered {a.scope?.departments || 0} departments, {a.scope?.systems || 0} systems, {a.scope?.vendors || 0} vendors and {a.scope?.flows || 0} flows as the map stood on {fmtDay(a.filed_on)}.</span>
          {a.notes && <span className="text-slate-500">{a.notes}</span>}
          <button type="button" onClick={() => setFiling(true)} className="ml-auto text-xs font-semibold text-orange-700">File a new analysis</button>
        </div>
      )}
      {filing && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-3">
          <p className="text-sm text-slate-700">File the risk analysis document. Its scope is read from the map as it stands today; the next one comes due a year from the date you give.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-slate-600">Date <input type="date" value={filedOn} onChange={(e) => setFiledOn(e.target.value)} className={`${inp} ml-1`} /></label>
            <label className="text-sm text-slate-600 cursor-pointer">Document <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="ml-1 text-xs" /></label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="One line about this year's analysis (optional)" className={`${inp} flex-1 min-w-[16rem]`} />
            <button type="button" disabled={busy} onClick={fileIt} className="px-4 py-1.5 rounded-lg bg-orange-500 text-white text-sm font-semibold disabled:opacity-50">{busy ? 'Filing…' : 'File it'}</button>
            {a && <button type="button" onClick={() => setFiling(false)} className="text-xs font-semibold text-slate-500">Cancel</button>}
          </div>
        </div>
      )}
      {a && (
        <div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Risks found, in the Risk register</span>
            <button type="button" onClick={() => setAdding((v) => !v)} className="ml-auto text-xs font-semibold text-orange-700">{adding ? 'Cancel' : 'Add a risk'}</button>
          </div>
          {adding && (
            <div className="mt-2 bg-slate-50 rounded-xl p-3 grid grid-cols-1 md:grid-cols-6 gap-2">
              <input value={rf.title} onChange={(e) => setRf({ ...rf, title: e.target.value })} placeholder="The risk, in one line" className={`${inp} md:col-span-2`} />
              <input value={rf.description} onChange={(e) => setRf({ ...rf, description: e.target.value })} placeholder="What could go wrong, and to whom" className={`${inp} md:col-span-2`} />
              <select value={rf.severity} onChange={(e) => setRf({ ...rf, severity: e.target.value })} className={inp}><option value="">Severity</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
              <div className="flex gap-2"><select value={rf.likelihood} onChange={(e) => setRf({ ...rf, likelihood: e.target.value })} className={`${inp} flex-1`}><option value="">Likelihood</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
              <button type="button" disabled={busy} onClick={addRisk} className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-sm font-semibold disabled:opacity-50">Add</button></div>
              <p className="md:col-span-6 text-xs text-slate-500">It lands in the Risk register, where its owner, mitigation and dates are managed.</p>
            </div>
          )}
          {risks && risks.length ? (
            <ul className="mt-2 divide-y divide-slate-100">
              {risks.map((r) => (
                <li key={r.id} className="py-2 flex items-center gap-3 text-sm">
                  <span className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${r.open ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{r.open ? 'Open' : 'Closed'}</span>
                  <span className="text-slate-800 font-medium">{r.risk_title}</span>
                  {r.owner && <span className="text-slate-500">· {r.owner}</span>}
                  {r.department && <span className="text-slate-500">· {r.department}</span>}
                  <span className="ml-auto text-xs text-slate-400">{fmtDay(r.created_at)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="mt-2 text-sm text-slate-500">No risks recorded from the analysis yet.</p>}
        </div>
      )}
      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}

function Reviews({ d, after }) {
  const needed = d.reviews?.needed || []; const mm = d.mismatches || [];
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState({});
  const dismiss = async (name) => { const r = await api('/prevention/mismatches/dismiss', { method: 'POST', body: { name, reason: reason[name] || '' } }); if (r.success) await after(); };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-4">
      {mm.length > 0 && (
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Named by a department, not in the FDR registry</div>
          <ul className="mt-1 divide-y divide-slate-100">
            {mm.map((m) => (
              <li key={m.name} className="py-2 flex items-center gap-3 flex-wrap text-sm">
                <span className="font-medium text-slate-800">{m.name}</span>
                <span className="text-slate-500">said by {[...new Set(m.said_by.map((s) => s.department))].join(', ')}</span>
                <span className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-slate-500">If it is a vendor, add it in FDR Oversight; the review follows.</span>
                  <input value={reason[m.name] || ''} onChange={(e) => setReason({ ...reason, [m.name]: e.target.value })} placeholder="Not a vendor because…" className="text-xs border border-slate-300 rounded-lg px-2 py-1 w-52" />
                  <button type="button" onClick={() => dismiss(m.name)} className="text-xs font-semibold text-slate-600">Not a vendor</button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {needed.length > 0 && (
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Needs a review</div>
          <ul className="mt-1 divide-y divide-slate-100">
            {needed.map((n) => (
              <li key={`${n.kind}:${n.ref}`} className="py-2">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-medium text-slate-800">{n.name}</span>
                  <span className="text-xs text-slate-500">{n.kind === 'vendor' ? 'Vendor' : 'System'} · {n.trigger}{n.started ? ' · started' : ''}</span>
                  <button type="button" onClick={() => setActive(active && active.ref === n.ref ? null : n)} className="ml-auto text-xs font-semibold text-orange-700">{active && active.ref === n.ref ? 'Close' : 'Review'}</button>
                </div>
                {active && active.ref === n.ref && <ReviewForm subject={n} onDone={async () => { setActive(null); await after(); }} />}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!needed.length && !mm.length && <p className="text-sm text-slate-500">Nothing waits. {d.reviews?.sealed || 0} review{d.reviews?.sealed === 1 ? '' : 's'} sealed.</p>}
    </div>
  );
}

const QUESTIONS = [
  ['data', 'What member information do they hold?'],
  ['purpose', 'What is it used for?'],
  ['minimum_necessary', 'Who can see it, and is that the least needed?'],
  ['safeguards', 'How is it protected?'],
  ['baa', 'What binds them?'],
  ['retention', 'How long is it kept, and how is it returned or destroyed?'],
];

function ReviewForm({ subject, onDone }) {
  const [p, setP] = useState(null);
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notNeeded, setNotNeeded] = useState('');
  useEffect(() => { api(`/prevention/reviews/${subject.kind}/${subject.ref}`).then((r) => { if (r.success) { setP(r); setAns(r.answers || {}); } else setErr(r.message || 'Could not load'); }); }, [subject.kind, subject.ref]);
  const send = async (action) => {
    setBusy(true); setErr('');
    const r = await api('/prevention/reviews', { method: 'POST', body: { subject_kind: subject.kind, subject_ref: subject.ref, subject_name: subject.name, trigger: subject.trigger, answers: ans, action, reason: notNeeded } });
    setBusy(false);
    if (!r.success) { setErr(r.message || 'Could not save'); return; }
    await onDone();
  };
  if (!p) return <div className="text-xs text-slate-400 py-2">{err || 'Reading what we know…'}</div>;
  return (
    <div className="mt-2 bg-slate-50 rounded-xl p-4 space-y-2">
      {p.sources?.length ? <p className="text-xs text-slate-500">Prefilled from: {p.sources.join(', ')}. Correct anything that is wrong, then seal.</p> : <p className="text-xs text-slate-500">Nothing on file yet to prefill; answer from what you know.</p>}
      {QUESTIONS.map(([id, text]) => (
        <label key={id} className="block">
          <span className="text-xs font-semibold text-slate-600">{text}</span>
          <textarea value={ans[id] || ''} onChange={(e) => setAns({ ...ans, [id]: e.target.value })} rows={1} className="mt-0.5 w-full text-sm border border-slate-300 rounded-lg px-3 py-1.5 text-slate-800 bg-white" />
        </label>
      ))}
      <div className="flex items-center gap-3 flex-wrap pt-1">
        <button type="button" disabled={busy} onClick={() => send('seal')} className="px-4 py-1.5 rounded-lg bg-orange-500 text-white text-sm font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Seal the review'}</button>
        <button type="button" disabled={busy} onClick={() => send('save')} className="text-xs font-semibold text-slate-600">Save for later</button>
        <span className="ml-auto flex items-center gap-2">
          <input value={notNeeded} onChange={(e) => setNotNeeded(e.target.value)} placeholder="No review needed because…" className="text-xs border border-slate-300 rounded-lg px-2 py-1 w-56" />
          <button type="button" disabled={busy || !notNeeded.trim()} onClick={() => send('not_needed')} className="text-xs font-semibold text-slate-600 disabled:opacity-40">Not needed</button>
        </span>
      </div>
      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}
